import { useId, useState } from 'react';
import type { ContinuousMonitor } from '../api';
import { indexerModeLabel, indexerStateLabel } from '../labels';
import { StatusBadge } from './ui';
import { Table } from './primitives/Table';
import { relativeTime } from './project/projectHealth';

/** One indexer as the last reading shows it (GAP-24). */
export interface IndexerRow {
  id: string;
  status: string;
  /** schedule, save, or empty when the indexer has no changelog view. */
  mode: string;
  /** Changelog entries waiting; null when not known or not applicable. */
  backlog: number | null;
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The indexers in a monitor's last result, or null when it has none (never read, or the database could not be read). */
export function indexersOf(metrics: Record<string, unknown> | undefined): IndexerRow[] | null {
  if (!metrics || !Array.isArray(metrics.indexers)) return null;
  const rows = metrics.indexers.flatMap((raw): IndexerRow[] => {
    const r = raw as Record<string, unknown>;
    const id = text(r?.id);
    if (!id) return [];
    const backlog = num(r.backlog);
    return [{ id, status: text(r.status), mode: text(r.mode), backlog: backlog !== undefined && backlog >= 0 ? backlog : null }];
  });
  return rows.length > 0 ? rows : null;
}

/**
 * isStaleIndexerReading: the last reading is older than three of the check's
 * own intervals (and at least 15 minutes). A reading that old says nothing
 * about the indexers now, so it is shown as not current, never as the state.
 */
export function isStaleIndexerReading(monitor: Pick<ContinuousMonitor, 'interval_seconds' | 'last_result' | 'enabled'>, now: number = Date.now()): boolean {
  if (!monitor.enabled || !monitor.last_result) return false;
  const at = Date.parse(monitor.last_result.checked_at);
  if (!Number.isFinite(at)) return true;
  return now - at > Math.max(3 * monitor.interval_seconds * 1000, 15 * 60 * 1000);
}

/**
 * IndexerDetails opens the "Magento indexers" check (GAP-24): when it last
 * read the database, and each indexer's state, indexing mode and changelog
 * backlog from that reading. A check that could not read says so and shows
 * no indexers.
 */
export function IndexerDetails({ monitor }: { monitor: ContinuousMonitor }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  if (!monitor.last_result) return null;
  const rows = indexersOf(monitor.last_result.metrics);
  const stale = isStaleIndexerReading(monitor);
  return (
    <div className="indexer-details" data-testid="indexer-details">
      <p className="indexer-details__meta">
        {stale ? <StatusBadge kind="check" value="unknown" label="Not current" testId="indexer-stale" /> : null}
        <span>
          {rows ? 'Read' : 'Last tried'} {relativeTime(monitor.last_result.checked_at)}
        </span>
        {rows ? (
          <button type="button" className="button button--secondary button--small" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide indexers' : `Show ${rows.length} indexers`}
          </button>
        ) : null}
      </p>
      {rows && open ? (
        <div id={panelId}>
          <Table label="Magento indexers" compact testId="indexer-table">
            <thead>
              <tr>
                <th scope="col">Indexer</th>
                <th scope="col">State</th>
                <th scope="col">Mode</th>
                <th scope="col">Waiting</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <th scope="row">
                    <code translate="no">{r.id}</code>
                  </th>
                  <td>
                    <StatusBadge kind="check" value={r.status === 'invalid' ? 'critical' : r.status === 'valid' ? 'healthy' : 'unknown'} label={indexerStateLabel[r.status] ?? (r.status || 'Not known')} variant="plain" />
                  </td>
                  <td>{indexerModeLabel[r.mode] ?? '—'}</td>
                  <td className="tabular-nums">{r.backlog === null ? '—' : r.backlog.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
